import React, { useState, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { userService } from '../_services';
import PasswordStrengthMeter, {
    isPasswordStrong,
    PASSWORD_NOT_STRONG_ENOUGH,
    passwordHasDisallowedWhitespace,
    PASSWORD_SPACE_NOT_ALLOWED,
} from '@/components/ui/PasswordStrengthMeter';
import { buildPasswordPolicyContext, getPasswordPolicyViolationMessage } from '@/_helpers/passwordPolicy';
import * as signalR from '@aspnet/signalr';

/**
 * Self-service change-password dialog.
 *
 * Props:
 *   closeResetPasswordPopup  {() => void}    – legacy prop; called on cancel / dialog close
 *   onSuccess                {() => void}    – optional; called ~1.5s after a successful change (user reads success msg first)
 *   onCancel                 {() => void}    – optional; called when Cancel is clicked
 *   open                     {boolean=true}   – optional; controls the Dialog open state (for orchestrator graceful close)
 *   mandatory                {boolean=false}  – optional; hides X button and suppresses Escape (used by orchestrator)
 */
function ChangePassword({ closeResetPasswordPopup, onSuccess, onCancel, open: openProp = true, mandatory = false }) {
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const [currentPwd, setCurrentPwd] = useState('');
    const [newPwd, setNewPwd] = useState('');
    const [confirmPwd, setConfirmPwd] = useState('');
    const [errors, setErrors] = useState({});
    const [showCurrentPwd, setShowCurrentPwd] = useState(false);
    const [showNewPwd, setShowNewPwd] = useState(false);
    const [showConfirmPwd, setShowConfirmPwd] = useState(false);
    
    const [userdata] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    });

    const policyContext = useMemo(
        () =>
            buildPasswordPolicyContext({
                email: userdata?.email,
                username: userdata?.username ?? userdata?.userName ?? '',
                fullName: userdata?.fullName ?? '',
            }),
        [userdata]
    );

    const resetSessionSignalR = useCallback(() => {
        try {
            const userAgent = window.navigator.userAgent;
            localStorage.setItem('ch_cred_upl_dt', new Date().toString());
            
            const connection = new signalR.HubConnectionBuilder()
                .withUrl("/resetSession", {
                    skipNegotiation: true,
                    transport: signalR.HttpTransportType.WebSockets
                })
                .build();
            
            connection.start().then(() => {
                connection.invoke("UpdateUserLoginPassword", userdata?.id, "demo", userAgent, userdata?.email)
                    .catch(err => {});
            });
        } catch (err) {
        }
    }, [userdata]);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        
        const newErrors = {};
        setError('');
        setSuccess(false);
        
        if (!currentPwd) {
            newErrors.currentPwd = 'Current password is required';
        } else if (userService.ValidateSpace(currentPwd)) {
            newErrors.currentPwd = 'Space not acceptable';
        }
        
        if (!newPwd) {
            newErrors.newPwd = 'New password is required';
        } else if (userService.ValidateSpace(newPwd)) {
            newErrors.newPwd = 'Space not acceptable';
        } else if (passwordHasDisallowedWhitespace(newPwd)) {
            newErrors.newPwd = PASSWORD_SPACE_NOT_ALLOWED;
        } else if (newPwd.length < 8) {
            newErrors.newPwd = 'Password must be at least 8 characters';
        } else if (currentPwd && newPwd === currentPwd) {
            newErrors.newPwd = 'New password must be different from your current password';
        } else if (!isPasswordStrong(newPwd, policyContext)) {
            newErrors.newPwd =
                getPasswordPolicyViolationMessage(newPwd, policyContext) || PASSWORD_NOT_STRONG_ENOUGH;
        }
        
        if (!confirmPwd) {
            newErrors.confirmPwd = 'Confirm new password is required';
        } else if (newPwd !== confirmPwd) {
            newErrors.confirmPwd = 'New Password and Confirm Password must match';
        }
        
        setErrors(newErrors);
        
        if (Object.keys(newErrors).length > 0) {
            return;
        }
        
        setLoading(true);
        
        try {
            const response = await fetch('/api/Account/requestpwdupdate', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'X-Browser-Url': window.location.href 
                },
                body: JSON.stringify({
                    UserEmail: userdata?.email,
                    CurrentPassword: currentPwd.trim(),
                    NewPassword: newPwd.trim(),
                    RequestURL: window.location.origin
                })
            });
            
            const data = await response.json();
            
            if (data?.length > 0) {
                if (data[0].status_code === "1") {
                    setSuccess(true);
                    setError(data[0].status_message);
                    resetSessionSignalR();
                    // Let the user read the success message, then close the dialog
                    setTimeout(() => {
                        onSuccess?.();
                        closeResetPasswordPopup?.();
                    }, 3000);
                } else {
                    setError(data[0].status_message);
                }
            } else {
                setError('Whoops, something went wrong. Please try again');
            }
        } catch (err) {
            setError('Whoops, something went wrong. Please try again');
        } finally {
            setLoading(false);
        }
    }, [currentPwd, newPwd, confirmPwd, userdata, resetSessionSignalR, policyContext, onSuccess, closeResetPasswordPopup]);

    const handleOpenChange = useCallback((open) => {
        if (!open) {
            closeResetPasswordPopup?.();
        }
    }, [closeResetPasswordPopup]);

    const handleCancel = useCallback(() => {
        if (onCancel) {
            onCancel();
        } else {
            closeResetPasswordPopup?.();
        }
    }, [onCancel, closeResetPasswordPopup]);

    return (
        <Dialog open={openProp} onOpenChange={handleOpenChange}>
            <DialogContent
                className={`sm:max-w-md${mandatory ? ' [&>button:last-child]:hidden' : ''}`}
                onEscapeKeyDown={mandatory ? (e) => e.preventDefault() : undefined}
            >
                <DialogHeader>
                    <DialogTitle>Reset Password</DialogTitle>
                </DialogHeader>
                
                <div className="flex flex-col gap-4">
                    <form id="change-password-form" onSubmit={handleSubmit} className="flex flex-col gap-4" autoComplete="off">
                        <div className="flex flex-col gap-2">
                            <Label htmlFor="currentPwd">Current Password</Label>
                            <div className="relative">
                                <Input
                                    id="currentPwd"
                                    type={showCurrentPwd ? 'text' : 'password'}
                                    value={currentPwd}
                                    onChange={(e) => { setCurrentPwd(e.target.value); setErrors(prev => ({ ...prev, currentPwd: '' })); }}
                                    autoComplete="off"
                                    maxLength={16}
                                    className={`pr-10${errors.currentPwd ? ' border-destructive' : ''}`}
                                />
                                <button
                                    type="button"
                                    tabIndex={-1}
                                    onClick={() => setShowCurrentPwd(v => !v)}
                                    className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground focus:outline-none"
                                    aria-label={showCurrentPwd ? 'Hide password' : 'Show password'}
                                >
                                    {showCurrentPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                            {errors.currentPwd && <p className="text-sm text-destructive">{errors.currentPwd}</p>}
                        </div>
                        
                        <div className="flex flex-col gap-2">
                            <Label htmlFor="newPwd">New Password</Label>
                            <div className="relative">
                                <Input
                                    id="newPwd"
                                    type={showNewPwd ? 'text' : 'password'}
                                    value={newPwd}
                                    onChange={(e) => { setNewPwd(e.target.value); setErrors(prev => ({ ...prev, newPwd: '' })); }}
                                    autoComplete="off"
                                    maxLength={16}
                                    className={`pr-10${errors.newPwd ? ' border-destructive' : ''}`}
                                />
                                <button
                                    type="button"
                                    tabIndex={-1}
                                    onClick={() => setShowNewPwd(v => !v)}
                                    className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground focus:outline-none"
                                    aria-label={showNewPwd ? 'Hide password' : 'Show password'}
                                >
                                    {showNewPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                            <PasswordStrengthMeter password={newPwd} policyContext={policyContext} />
                            {errors.newPwd && <p className="text-sm text-destructive">{errors.newPwd}</p>}
                        </div>
                        
                        <div className="flex flex-col gap-2">
                            <Label htmlFor="confirmPwd">Confirm New Password</Label>
                            <div className="relative">
                                <Input
                                    id="confirmPwd"
                                    type={showConfirmPwd ? 'text' : 'password'}
                                    value={confirmPwd}
                                    onChange={(e) => { setConfirmPwd(e.target.value); setErrors(prev => ({ ...prev, confirmPwd: '' })); }}
                                    autoComplete="off"
                                    maxLength={16}
                                    className={`pr-10${errors.confirmPwd ? ' border-destructive' : ''}`}
                                />
                                <button
                                    type="button"
                                    tabIndex={-1}
                                    onClick={() => setShowConfirmPwd(v => !v)}
                                    className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground focus:outline-none"
                                    aria-label={showConfirmPwd ? 'Hide password' : 'Show password'}
                                >
                                    {showConfirmPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                            {errors.confirmPwd && <p className="text-sm text-destructive">{errors.confirmPwd}</p>}
                        </div>
                        
                        {error && (
                            <div className={`flex items-center gap-2 p-3 rounded-md ${success ? 'bg-green-50 text-green-700' : 'bg-destructive/10 text-destructive'}`}>
                                {success && <CheckCircle className="h-4 w-4" />}
                                <p className="text-sm">{error}</p>
                            </div>
                        )}
                    </form>
                </div>
                <DialogFooter>
                    <Button type="submit" form="change-password-form" disabled={loading}>
                                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                Submit
                            </Button>
                            <Button type="button" variant="secondary" onClick={handleCancel}>
                                Cancel
                            </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default ChangePassword;
